import { useMemo } from "react";

import { bpsToUiDecimals, toTokenAmount, tokenAmountToUiNumber } from "@bridgesplit/utils";

import { useTokenListContext } from "../../api/token-list";
import { EarnPortfolioSummaryView } from "../../types";
import { useEarnPortfolioSummaryDomain } from "../domain/earn-summary";

/**
 * UI adapter for the earn portfolio summary. Rescales `apyBps` to a decimal, pairs each atomic
 * amount with its decimals, and derives USD PnL from the BE-provided value/native ratio (the
 * summary endpoint intentionally omits USD PnL).
 */
export function useEarnPortfolioSummary(wallet: string | undefined): {
    data: EarnPortfolioSummaryView[] | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const domain = useEarnPortfolioSummaryDomain(wallet);
    const { getDecimals } = useTokenListContext();

    const data = useMemo(
        () =>
            domain.data?.map((group): EarnPortfolioSummaryView => {
                const decimals = getDecimals(group.depositMint);
                const positionValue = toTokenAmount(group.positionValueAssets, decimals);
                const unrealizedPnl = toTokenAmount(group.unrealizedPnlAssets, decimals);
                const realizedPnl = toTokenAmount(group.realizedPnlAssets, decimals);

                // Native → USD via the BE's own value/native ratio, so derived PnL USD stays
                // consistent with `positionValueUsd` without a separate oracle fetch.
                const uiValue = tokenAmountToUiNumber(positionValue);
                const pricePerToken = uiValue && uiValue > 0 ? group.positionValueUsd / uiValue : undefined;
                const toUsd = (amount: typeof positionValue) => {
                    const ui = tokenAmountToUiNumber(amount);
                    return ui != null && pricePerToken != null ? ui * pricePerToken : undefined;
                };

                return {
                    depositMint: group.depositMint,
                    vaultAddresses: group.vaultAddresses,
                    count: group.vaultAddresses.length,
                    positionValue,
                    positionValueUsd: group.positionValueUsd,
                    costBasis: toTokenAmount(group.costBasisAssets, decimals),
                    unrealizedPnl,
                    unrealizedPnlUsd: toUsd(unrealizedPnl),
                    realizedPnl,
                    realizedPnlUsd: toUsd(realizedPnl),
                    apy: group.apyBps == null ? undefined : bpsToUiDecimals(group.apyBps, "bps")
                };
            }),
        [domain.data, getDecimals]
    );

    return {
        data,
        isLoading: domain.isLoading,
        isFetching: domain.isFetching
    };
}
